import { useCallback, useState } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';
import { arrayMove } from '@ts-core/core/components/Sort';

import { useAvailableExercises } from '../../hooks';
import { PracticeSession, PracticeExercise, PracticeSubject } from '../../types';
import {
  createPracticeSessionMutation,
  CreatePracticeSessionMutationVariables,
  CreatePracticeSessionMutationResult,
} from '../../graphql/mutations/createPracticeSessionMutation';

import { PracticeSessionFormProps } from './PracticeSessionForm';

export interface PracticeSessionFormHookInput {
  courseId: string;
  onSuccess: (practiceSession: PracticeSession) => void;
}

export interface PracticeSessionFormHookPayload {
  formProps: PracticeSessionFormProps;
}

export default function usePracticeSessionForm(input: PracticeSessionFormHookInput): PracticeSessionFormHookPayload {
  const { onSuccess, courseId } = input;
  const [selectedExercises, setSelectedExercises] = useState<PracticeExercise[]>([]);
  const { showErrorNotification } = useSnackbarNotifications();
  const { loading, subjects } = useAvailableExercises({ courseId });

  const isExerciseSelected = useCallback(
    (exercise: PracticeExercise) => {
      return selectedExercises.find((e) => e._id === exercise._id) !== undefined;
    },
    [selectedExercises]
  );

  const onRemoveExercise = useCallback((exercise: PracticeExercise) => {
    setSelectedExercises((state) => state.filter((e) => e._id !== exercise._id));
  }, []);

  const onSelectExercise = useCallback(
    (exercise: PracticeExercise) => {
      if (isExerciseSelected(exercise)) {
        onRemoveExercise(exercise);
      } else {
        setSelectedExercises((state) => [...state, exercise]);
      }
    },
    [isExerciseSelected, onRemoveExercise]
  );

  const onSelectSubjectExercises = useCallback((subject: PracticeSubject) => {
    setSelectedExercises([...subject.exercises]);
  }, []);

  const onRemoveAllExercises = useCallback(() => {
    setSelectedExercises([]);
  }, []);

  const [createPracticeSession, { loading: createPracticeSessionLoading }] = useMutation<
    CreatePracticeSessionMutationResult,
    CreatePracticeSessionMutationVariables
  >(createPracticeSessionMutation, { onError: showErrorNotification });
  const onCreatePracticeSession = useCallback(() => {
    const [exercise] = selectedExercises;
    const name = exercise.subjectPath;

    createPracticeSession({
      variables: {
        courseId,
        input: {
          name,
          exercises: selectedExercises.map((exercise) => exercise._id),
        },
      },
    }).then(({ data }) => {
      if (data?.practiceSession) {
        onSuccess(data.practiceSession);
      }
    });
  }, [selectedExercises, courseId, createPracticeSession, onSuccess]);

  const onSortExercises = useCallback((from: number, to: number) => {
    setSelectedExercises((state) => arrayMove(state, from, to));
  }, []);

  return {
    formProps: {
      isExerciseSelected,
      onSelectExercise,
      onSortExercises,
      onSelectSubjectExercises,
      onRemoveExercise,
      onRemoveAllExercises,

      onCreatePracticeSession,
      createPracticeSessionLoading,

      selectedExercises,

      subjects,
      loading,
    },
  };
}
